/**
 * Badge — small pill for status / role / dept labels.
 *
 * Phase-2 Wave-2 W2.T6 primitive. Variants cover both shadcn defaults
 * (default / secondary / destructive / outline) AND warm semantic tones
 * the Phase-2 Employees page needs:
 *
 *   - active     — green wash for ACTIVE users.
 *   - pending    — amber wash for PENDING (invite not yet consumed).
 *   - archived   — muted neutral for ARCHIVED.
 *   - admin / supervisor / manager / employee — role-tier accents that
 *     match the warm-theme reference.
 *
 * Why CVA: keeps the variant matrix declarative + lets consumers compose
 * `<Badge variant="pending" className="…">` without having to remember
 * which token belongs to which state.
 */
import * as React from 'react';
import { cva, type VariantProps } from 'class-variance-authority';

import { cn } from '@/lib/utils';

const badgeVariants = cva(
  'inline-flex items-center rounded-sm border px-2 py-0.5 text-xs font-medium transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-ring',
  {
    variants: {
      variant: {
        default: 'border-transparent bg-primary text-primary-foreground',
        secondary: 'border-transparent bg-secondary text-secondary-foreground',
        destructive: 'border-transparent bg-destructive text-destructive-foreground',
        outline: 'border-line bg-surface text-ink',
        // Warm semantic — status states.
        active: 'border-transparent bg-ok-wash text-[rgb(var(--ok))]',
        pending: 'border-transparent bg-warn-wash text-[rgb(var(--warn))]',
        archived: 'border-line bg-surface-2 text-muted',
        /**
         * Ф3 · В3a — признаки СРОКА работы. Отдельные варианты, а не
         * переиспользование `pending` / `destructive`, и причина в контрасте:
         * `pending` даёт `--warn` на `--warn-wash` = **2.56:1** при норме 4.5,
         * то есть подпись «Срочно» была бы нечитаемой. Менять сам `pending`
         * нельзя — через него идут состояния приглашений на других экранах,
         * которые эта волна не проверяет (наследие Ф2, вынесено долгом).
         *
         * Здесь:
         *   - `overdue` — `--danger` на `--danger-wash` = 5.32:1 ✓ AA;
         *   - `urgent`  — `--ink` на `--warn-wash` = 15.0:1 ✓ AAA, при этом
         *     рамка `--warn` сохраняет предупреждающий ХАРАКТЕР, не жертвуя
         *     читаемостью текста.
         *
         * Оба различимы НЕ ТОЛЬКО ЦВЕТОМ: у них разная рамка, разный вес фона
         * и разный глиф в самом компоненте — в тёплой теме `--danger` близок к
         * `--accent`, и опора на цвет здесь запрещена отдельно.
         */
        overdue:
          'border-[rgb(var(--danger))] bg-danger-wash text-[rgb(var(--danger))] font-semibold',
        urgent: 'border-[rgb(var(--warn))] bg-warn-wash text-ink',
        /**
         * Ф7 · В11-b — ОТРИЦАТЕЛЬНОЕ СОСТОЯНИЕ, НЕ СВЯЗАННОЕ СО СРОКОМ
         * (первый потребитель — «Отменён» у договора).
         *
         * ⚠️ ПОЧЕМУ НЕ `overdue`, хотя цвета те же. `overdue` — про СРОК: он
         * несёт `font-semibold` и сплошную рамку сознательно, чтобы
         * просроченное вырывалось из строки списка. Состояние договора так
         * кричать не должно — оно не требует действия, а сообщает факт.
         * Назвать «Отменён» просроченным значило бы описать одним именем два
         * разных правила, и следующая правка `overdue` (веса, рамки, глифа)
         * поехала бы заодно и сюда.
         *
         * ⚠️ ПОЧЕМУ НЕ `destructive`: там сплошная заливка `--destructive`,
         * вес которой в тёплой теме равен кнопке удаления. Значок состояния
         * не действие.
         *
         * Контраст: `--danger` на `--danger-wash` = 5.32 : 1 (значение
         * посчитано у `overdue` выше — пара токенов та же) ✓ AA.
         */
        danger: 'border-[rgb(var(--danger))] bg-danger-wash text-[rgb(var(--danger))]',
        // Warm semantic — role tiers.
        admin: 'border-transparent bg-accent-wash text-accent-ink',
        supervisor:
          'border-transparent bg-[rgb(var(--accent-2))] text-[rgb(var(--accent-foreground))]',
        manager: 'border-transparent bg-info-wash text-[rgb(var(--info))]',
        employee: 'border-line bg-surface text-ink-2',
      },
    },
    defaultVariants: { variant: 'default' },
  },
);

export interface BadgeProps
  extends React.HTMLAttributes<HTMLSpanElement>, VariantProps<typeof badgeVariants> {}

const Badge = React.forwardRef<HTMLSpanElement, BadgeProps>(
  ({ className, variant, ...props }, ref) => (
    <span ref={ref} className={cn(badgeVariants({ variant }), className)} {...props} />
  ),
);
Badge.displayName = 'Badge';

export { Badge, badgeVariants };
